import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { Copy, Download, ImageUp, Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import "./index.css";

type PresetKey = "x_sharp" | "x_large" | "x_standard" | "square";
type ThemeKey = "sunrise" | "ocean" | "forest" | "slate";

type ComposerSettings = {
  preset: PresetKey;
  theme: ThemeKey;
  outerPaddingPct: number;
  innerPaddingPct: number;
  radiusPx: number;
  shadowStrength: number;
  imageScale: number;
};

type Profile = {
  id: string;
  name: string;
  createdAt: number;
  updatedAt: number;
  settings: ComposerSettings;
};

type ProfileStore = {
  version: 1;
  activeProfileId: string;
  profiles: Profile[];
};

type LoadedImage = {
  element: HTMLImageElement;
  objectUrl: string;
  name: string;
  width: number;
  height: number;
};

const STORAGE_KEY = "xshot.profiles.v1";

const PRESETS: Record<PresetKey, { label: string; width: number; height: number }> = {
  x_sharp: { label: "X Sharp (2400 x 1350)", width: 2400, height: 1350 },
  x_large: { label: "X Large (1600 x 900)", width: 1600, height: 900 },
  x_standard: { label: "X Standard (1200 x 675)", width: 1200, height: 675 },
  square: { label: "Square (1200 x 1200)", width: 1200, height: 1200 },
};

const THEMES: Record<ThemeKey, { label: string; start: string; end: string; veil: string; cardTint: string }> = {
  sunrise: {
    label: "Sunrise",
    start: "#ffb36b",
    end: "#ff5f7f",
    veil: "rgba(17, 24, 39, 0.22)",
    cardTint: "rgba(255, 251, 245, 0.95)",
  },
  ocean: {
    label: "Ocean",
    start: "#0ea5e9",
    end: "#1d4ed8",
    veil: "rgba(2, 6, 23, 0.3)",
    cardTint: "rgba(246, 252, 255, 0.94)",
  },
  forest: {
    label: "Forest",
    start: "#34d399",
    end: "#065f46",
    veil: "rgba(3, 7, 18, 0.24)",
    cardTint: "rgba(244, 251, 248, 0.94)",
  },
  slate: {
    label: "Slate",
    start: "#334155",
    end: "#0f172a",
    veil: "rgba(15, 23, 42, 0.2)",
    cardTint: "rgba(248, 250, 252, 0.95)",
  },
};

const DEFAULT_SETTINGS: ComposerSettings = {
  preset: "x_sharp",
  theme: "sunrise",
  outerPaddingPct: 8,
  innerPaddingPct: 4,
  radiusPx: 30,
  shadowStrength: 62,
  imageScale: 100,
};

function makeId() {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function makeProfile(name: string, settings: ComposerSettings = DEFAULT_SETTINGS): Profile {
  const now = Date.now();
  return {
    id: makeId(),
    name,
    createdAt: now,
    updatedAt: now,
    settings: { ...settings },
  };
}

function makeDefaultStore(): ProfileStore {
  const profile = makeProfile("X Sharp Default");
  return {
    version: 1,
    activeProfileId: profile.id,
    profiles: [profile],
  };
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function sanitizeSettings(settings: Partial<ComposerSettings> | undefined): ComposerSettings {
  const preset = settings?.preset && PRESETS[settings.preset] ? settings.preset : DEFAULT_SETTINGS.preset;
  const theme = settings?.theme && THEMES[settings.theme] ? settings.theme : DEFAULT_SETTINGS.theme;
  return {
    preset,
    theme,
    outerPaddingPct: clamp(Number(settings?.outerPaddingPct ?? DEFAULT_SETTINGS.outerPaddingPct), 2, 18),
    innerPaddingPct: clamp(Number(settings?.innerPaddingPct ?? DEFAULT_SETTINGS.innerPaddingPct), 1, 12),
    radiusPx: clamp(Number(settings?.radiusPx ?? DEFAULT_SETTINGS.radiusPx), 8, 80),
    shadowStrength: clamp(Number(settings?.shadowStrength ?? DEFAULT_SETTINGS.shadowStrength), 0, 100),
    imageScale: clamp(Number(settings?.imageScale ?? DEFAULT_SETTINGS.imageScale), 40, 100),
  };
}

function loadStore(): ProfileStore {
  if (typeof localStorage === "undefined") return makeDefaultStore();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return makeDefaultStore();
    const parsed = JSON.parse(raw) as Partial<ProfileStore>;
    if (parsed.version !== 1 || !Array.isArray(parsed.profiles) || parsed.profiles.length === 0) {
      return makeDefaultStore();
    }

    const profiles = parsed.profiles
      .filter(profile => profile && typeof profile.id === "string" && typeof profile.name === "string")
      .map(profile => ({
        id: profile.id,
        name: profile.name.trim() || "Untitled Profile",
        createdAt: Number(profile.createdAt ?? Date.now()),
        updatedAt: Number(profile.updatedAt ?? Date.now()),
        settings: sanitizeSettings(profile.settings),
      }));

    if (profiles.length === 0) return makeDefaultStore();

    const activeProfileId = profiles.some(profile => profile.id === parsed.activeProfileId)
      ? (parsed.activeProfileId as string)
      : (profiles[0]?.id ?? makeDefaultStore().activeProfileId);

    return {
      version: 1,
      activeProfileId,
      profiles,
    };
  } catch {
    return makeDefaultStore();
  }
}

function roundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, radius: number) {
  const r = clamp(radius, 0, Math.min(w, h) / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function drawImageCover(
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  targetX: number,
  targetY: number,
  targetW: number,
  targetH: number,
) {
  const ratio = Math.max(targetW / image.naturalWidth, targetH / image.naturalHeight);
  const drawW = image.naturalWidth * ratio;
  const drawH = image.naturalHeight * ratio;
  const drawX = targetX + (targetW - drawW) / 2;
  const drawY = targetY + (targetH - drawH) / 2;
  ctx.drawImage(image, drawX, drawY, drawW, drawH);
}

function renderComposition(settings: ComposerSettings, image: HTMLImageElement | null) {
  const preset = PRESETS[settings.preset];
  const theme = THEMES[settings.theme];

  const canvas = document.createElement("canvas");
  canvas.width = preset.width;
  canvas.height = preset.height;
  const ctx = canvas.getContext("2d");

  if (!ctx) return canvas;

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
  gradient.addColorStop(0, theme.start);
  gradient.addColorStop(1, theme.end);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  if (image) {
    ctx.save();
    ctx.globalAlpha = 0.24;
    ctx.filter = `blur(${Math.round(Math.min(canvas.width, canvas.height) * 0.03)}px) saturate(120%)`;
    drawImageCover(ctx, image, 0, 0, canvas.width, canvas.height);
    ctx.restore();
  }

  ctx.fillStyle = theme.veil;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const outerPaddingPx = Math.round((Math.min(canvas.width, canvas.height) * settings.outerPaddingPct) / 100);
  const cardX = outerPaddingPx;
  const cardY = outerPaddingPx;
  const cardW = canvas.width - outerPaddingPx * 2;
  const cardH = canvas.height - outerPaddingPx * 2;
  const radius = clamp(settings.radiusPx, 8, Math.min(cardW, cardH) / 2);

  ctx.save();
  const shadowBlur = Math.round((settings.shadowStrength / 100) * 88);
  ctx.shadowColor = `rgba(0, 0, 0, ${0.12 + settings.shadowStrength / 220})`;
  ctx.shadowBlur = shadowBlur;
  ctx.shadowOffsetY = Math.round(shadowBlur * 0.3);
  roundedRect(ctx, cardX, cardY, cardW, cardH, radius);
  ctx.fillStyle = theme.cardTint;
  ctx.fill();
  ctx.restore();

  roundedRect(ctx, cardX, cardY, cardW, cardH, radius);
  ctx.lineWidth = 2;
  ctx.strokeStyle = "rgba(255,255,255,0.36)";
  ctx.stroke();

  const innerPaddingPx = Math.round((Math.min(cardW, cardH) * settings.innerPaddingPct) / 100);
  const mediaX = cardX + innerPaddingPx;
  const mediaY = cardY + innerPaddingPx;
  const mediaW = cardW - innerPaddingPx * 2;
  const mediaH = cardH - innerPaddingPx * 2;
  const mediaRadius = clamp(radius * 0.62, 6, Math.min(mediaW, mediaH) / 2);

  roundedRect(ctx, mediaX, mediaY, mediaW, mediaH, mediaRadius);
  ctx.fillStyle = "rgba(15, 23, 42, 0.08)";
  ctx.fill();

  if (image) {
    const fitRatio = Math.min(mediaW / image.naturalWidth, mediaH / image.naturalHeight);
    const scaledRatio = fitRatio * (settings.imageScale / 100);
    const drawW = image.naturalWidth * scaledRatio;
    const drawH = image.naturalHeight * scaledRatio;
    const drawX = mediaX + (mediaW - drawW) / 2;
    const drawY = mediaY + (mediaH - drawH) / 2;

    ctx.save();
    roundedRect(ctx, mediaX, mediaY, mediaW, mediaH, mediaRadius);
    ctx.clip();
    ctx.fillStyle = "rgba(2, 6, 23, 0.06)";
    ctx.fillRect(mediaX, mediaY, mediaW, mediaH);
    ctx.drawImage(image, drawX, drawY, drawW, drawH);
    ctx.restore();
  } else {
    ctx.save();
    roundedRect(ctx, mediaX, mediaY, mediaW, mediaH, mediaRadius);
    ctx.clip();
    ctx.fillStyle = "rgba(2, 6, 23, 0.05)";
    ctx.fillRect(mediaX, mediaY, mediaW, mediaH);
    ctx.restore();

    ctx.fillStyle = "rgba(15, 23, 42, 0.64)";
    ctx.font = `${Math.round(Math.min(canvas.width, canvas.height) * 0.03)}px ui-sans-serif, system-ui, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("Upload or paste a screenshot", canvas.width / 2, canvas.height / 2);
  }

  return canvas;
}

export function App() {
  const [profileStore, setProfileStore] = useState<ProfileStore>(() => loadStore());
  const [loadedImage, setLoadedImage] = useState<LoadedImage | null>(null);
  const [status, setStatus] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const activeProfile = useMemo(() => {
    return profileStore.profiles.find(profile => profile.id === profileStore.activeProfileId) ?? profileStore.profiles[0];
  }, [profileStore]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(profileStore));
    } catch {
      setStatus("Could not save profiles to local storage.");
    }
  }, [profileStore]);

  useEffect(() => {
    return () => {
      if (loadedImage) URL.revokeObjectURL(loadedImage.objectUrl);
    };
  }, [loadedImage]);

  useEffect(() => {
    const onPaste = async (event: ClipboardEvent) => {
      const items = event.clipboardData?.items;
      if (!items) return;
      const imageItem = Array.from(items).find(item => item.type.startsWith("image/"));
      if (!imageItem) return;
      const file = imageItem.getAsFile();
      if (!file) return;
      event.preventDefault();
      await importFile(file, "Pasted image");
    };

    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, []);

  useEffect(() => {
    if (!activeProfile) return;
    const preview = previewCanvasRef.current;
    if (!preview) return;
    const composed = renderComposition(activeProfile.settings, loadedImage?.element ?? null);
    preview.width = composed.width;
    preview.height = composed.height;
    const ctx = preview.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, preview.width, preview.height);
    ctx.drawImage(composed, 0, 0);
  }, [activeProfile, loadedImage]);

  if (!activeProfile) {
    return null;
  }

  const activePreset = PRESETS[activeProfile.settings.preset];

  const updateActiveProfile = (nextSettings: Partial<ComposerSettings>) => {
    setProfileStore(previous => ({
      ...previous,
      profiles: previous.profiles.map(profile =>
        profile.id === previous.activeProfileId
          ? {
              ...profile,
              updatedAt: Date.now(),
              settings: sanitizeSettings({ ...profile.settings, ...nextSettings }),
            }
          : profile,
      ),
    }));
  };

  const importFile = async (file: File, sourceLabel: string) => {
    if (!file.type.startsWith("image/")) {
      setStatus("That file is not an image.");
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.src = objectUrl;
    image.decoding = "async";

    try {
      await image.decode();
      setLoadedImage(previous => {
        if (previous) URL.revokeObjectURL(previous.objectUrl);
        return {
          element: image,
          objectUrl,
          name: file.name || sourceLabel,
          width: image.naturalWidth,
          height: image.naturalHeight,
        };
      });
      setStatus(`${sourceLabel} loaded at ${image.naturalWidth} x ${image.naturalHeight}.`);
    } catch {
      URL.revokeObjectURL(objectUrl);
      setStatus("Unable to decode image file.");
    }
  };

  const handleDownload = async () => {
    const composed = renderComposition(activeProfile.settings, loadedImage?.element ?? null);
    const blob = await new Promise<Blob | null>(resolve => composed.toBlob(resolve, "image/png", 1));
    if (!blob) {
      setStatus("Could not generate PNG file.");
      return;
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `xshot-${activePreset.width}x${activePreset.height}.png`;
    a.click();
    URL.revokeObjectURL(url);
    setStatus("PNG exported.");
  };

  const handleCopy = async () => {
    if (!("clipboard" in navigator) || !("ClipboardItem" in window)) {
      setStatus("Copy image is not supported in this browser.");
      return;
    }

    const composed = renderComposition(activeProfile.settings, loadedImage?.element ?? null);
    const blob = await new Promise<Blob | null>(resolve => composed.toBlob(resolve, "image/png", 1));
    if (!blob) {
      setStatus("Could not copy image.");
      return;
    }

    await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
    setStatus("PNG copied to clipboard.");
  };

  const createProfile = () => {
    const index = profileStore.profiles.length + 1;
    const profile = makeProfile(`Profile ${index}`, activeProfile.settings);
    setProfileStore(previous => ({
      ...previous,
      activeProfileId: profile.id,
      profiles: [...previous.profiles, profile],
    }));
    setStatus(`Created ${profile.name}.`);
  };

  const renameProfile = () => {
    const nextName = window.prompt("Rename profile", activeProfile.name);
    if (!nextName) return;
    const trimmed = nextName.trim();
    if (!trimmed) return;
    setProfileStore(previous => ({
      ...previous,
      profiles: previous.profiles.map(profile =>
        profile.id === previous.activeProfileId ? { ...profile, name: trimmed, updatedAt: Date.now() } : profile,
      ),
    }));
    setStatus("Profile renamed.");
  };

  const duplicateProfile = () => {
    const profile = makeProfile(`${activeProfile.name} Copy`, activeProfile.settings);
    setProfileStore(previous => ({
      ...previous,
      activeProfileId: profile.id,
      profiles: [...previous.profiles, profile],
    }));
    setStatus("Profile duplicated.");
  };

  const deleteProfile = () => {
    if (profileStore.profiles.length <= 1) {
      setStatus("Keep at least one profile.");
      return;
    }
    const shouldDelete = window.confirm(`Delete profile \"${activeProfile.name}\"?`);
    if (!shouldDelete) return;

    setProfileStore(previous => {
      const profiles = previous.profiles.filter(profile => profile.id !== previous.activeProfileId);
      const nextActive = profiles[0]?.id ?? "";
      return {
        ...previous,
        activeProfileId: nextActive,
        profiles,
      };
    });
    setStatus("Profile deleted.");
  };

  const resetProfile = () => {
    setProfileStore(previous => ({
      ...previous,
      profiles: previous.profiles.map(profile =>
        profile.id === previous.activeProfileId
          ? { ...profile, updatedAt: Date.now(), settings: { ...DEFAULT_SETTINGS } }
          : profile,
      ),
    }));
    setStatus("Profile reset to defaults.");
  };

  return (
    <div className="mx-auto grid w-full max-w-[1260px] gap-6 p-4 md:p-8 xl:grid-cols-[1.2fr_0.8fr]">
      <Card className="border-white/30 bg-white/45 shadow-xl backdrop-blur-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-2xl">X Screenshot Composer</CardTitle>
          <p className="text-sm text-muted-foreground">
            Upload, paste, or drag an image and export a sharp, X-ready PNG with smart framing.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div
            onDragOver={event => {
              event.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={async event => {
              event.preventDefault();
              setIsDragging(false);
              const file = event.dataTransfer.files[0];
              if (!file) return;
              await importFile(file, "Dropped image");
            }}
            className={cn(
              "rounded-xl border-2 border-dashed p-4 transition",
              isDragging ? "border-primary bg-primary/10" : "border-border bg-white/45",
            )}
          >
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" onClick={() => fileInputRef.current?.click()}>
                <ImageUp className="size-4" />
                Upload Screenshot
              </Button>
              <Button type="button" variant="secondary" onClick={handleCopy}>
                <Copy className="size-4" />
                Copy PNG
              </Button>
              <Button type="button" variant="secondary" onClick={handleDownload}>
                <Download className="size-4" />
                Download PNG
              </Button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={async event => {
                const file = event.currentTarget.files?.[0];
                if (!file) return;
                await importFile(file, "Uploaded image");
                event.currentTarget.value = "";
              }}
            />
            <p className="mt-3 text-xs text-muted-foreground">
              Tip: paste directly with Ctrl/Cmd+V. Input image stays fully visible with contain-fit framing.
            </p>
            {loadedImage && (
              <p className="mt-2 text-xs text-foreground/80">
                Source: {loadedImage.name} ({loadedImage.width} x {loadedImage.height})
              </p>
            )}
          </div>

          <div className="overflow-hidden rounded-xl border border-white/40 bg-slate-950/90">
            <canvas
              ref={previewCanvasRef}
              className="block h-auto w-full"
              style={{ aspectRatio: `${activePreset.width} / ${activePreset.height}` }}
            />
          </div>

          <p className="text-xs text-muted-foreground">Export size: {activePreset.width} x {activePreset.height}</p>
        </CardContent>
      </Card>

      <Card className="border-white/30 bg-white/50 shadow-xl backdrop-blur-sm">
        <CardHeader className="pb-3">
          <CardTitle>Profile & Controls</CardTitle>
          <p className="text-sm text-muted-foreground">Profiles are stored in localStorage on this browser.</p>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <Label>Profile</Label>
            <Select
              value={activeProfile.id}
              onValueChange={value => setProfileStore(previous => ({ ...previous, activeProfileId: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Choose profile" />
              </SelectTrigger>
              <SelectContent>
                {profileStore.profiles.map(profile => (
                  <SelectItem key={profile.id} value={profile.id}>
                    {profile.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex flex-wrap gap-2 pt-1">
              <Button type="button" variant="secondary" size="sm" onClick={createProfile}>
                <Plus className="size-4" />
                New
              </Button>
              <Button type="button" variant="secondary" size="sm" onClick={renameProfile}>
                Rename
              </Button>
              <Button type="button" variant="secondary" size="sm" onClick={duplicateProfile}>
                Duplicate
              </Button>
              <Button type="button" variant="secondary" size="sm" onClick={resetProfile}>
                Reset
              </Button>
              <Button type="button" variant="destructive" size="sm" onClick={deleteProfile}>
                <Trash2 className="size-4" />
                Delete
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Export Preset</Label>
            <Select
              value={activeProfile.settings.preset}
              onValueChange={value => updateActiveProfile({ preset: value as PresetKey })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Choose preset" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(PRESETS).map(([key, preset]) => (
                  <SelectItem key={key} value={key}>
                    {preset.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Theme</Label>
            <Select value={activeProfile.settings.theme} onValueChange={value => updateActiveProfile({ theme: value as ThemeKey })}>
              <SelectTrigger>
                <SelectValue placeholder="Choose theme" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(THEMES).map(([key, theme]) => (
                  <SelectItem key={key} value={key}>
                    {theme.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="outerPadding">Outer Padding ({activeProfile.settings.outerPaddingPct.toFixed(1)}%)</Label>
            <input
              id="outerPadding"
              type="range"
              min={2}
              max={18}
              step={0.5}
              value={activeProfile.settings.outerPaddingPct}
              onChange={event => updateActiveProfile({ outerPaddingPct: Number(event.currentTarget.value) })}
              className="w-full"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="innerPadding">Frame Padding ({activeProfile.settings.innerPaddingPct.toFixed(1)}%)</Label>
            <input
              id="innerPadding"
              type="range"
              min={1}
              max={12}
              step={0.5}
              value={activeProfile.settings.innerPaddingPct}
              onChange={event => updateActiveProfile({ innerPaddingPct: Number(event.currentTarget.value) })}
              className="w-full"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="radius">Corner Radius ({Math.round(activeProfile.settings.radiusPx)}px)</Label>
            <input
              id="radius"
              type="range"
              min={8}
              max={80}
              step={1}
              value={activeProfile.settings.radiusPx}
              onChange={event => updateActiveProfile({ radiusPx: Number(event.currentTarget.value) })}
              className="w-full"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="shadow">Shadow ({Math.round(activeProfile.settings.shadowStrength)}%)</Label>
            <input
              id="shadow"
              type="range"
              min={0}
              max={100}
              step={1}
              value={activeProfile.settings.shadowStrength}
              onChange={event => updateActiveProfile({ shadowStrength: Number(event.currentTarget.value) })}
              className="w-full"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="scale">Image Scale ({Math.round(activeProfile.settings.imageScale)}%)</Label>
            <input
              id="scale"
              type="range"
              min={40}
              max={100}
              step={1}
              value={activeProfile.settings.imageScale}
              onChange={event => updateActiveProfile({ imageScale: Number(event.currentTarget.value) })}
              className="w-full"
            />
          </div>

          <p className="rounded-md bg-muted/70 px-3 py-2 text-xs text-muted-foreground">
            {status || "Ready. Paste or upload a screenshot to start."}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

export default App;
